"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ArrowRight, Play } from "lucide-react";
import { useTranslation } from "react-i18next";
const HeroScene = dynamic(() => import("./hero-scene").then(m => m.HeroScene), {
  ssr: false,
  loading: () => <div className="h-full w-full" />
});
function useIsDesktop() {
  const $ = _c(2);
  const [isDesktop, setIsDesktop] = useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  return isDesktop;
}
function useDeferredRender(t0) {
  const $ = _c(3);
  const delay = t0 === undefined ? 0 : t0;
  const [ready, setReady] = useState(false);
  let t1;
  let t2;
  if ($[0] !== delay) {
    t1 = () => {
      if ("requestIdleCallback" in window) {
        const id = requestIdleCallback(() => setReady(true), {
          timeout: delay + 2000
        });
        return () => cancelIdleCallback(id);
      }
      const timer = setTimeout(() => setReady(true), delay);
      return () => clearTimeout(timer);
    };
    t2 = [delay];
    $[0] = delay;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  return ready;
}
export function HeroSection() {
  const $ = _c(67);
  const {
    t
  } = useTranslation();
  const isDesktop = useIsDesktop();
  const sceneReady = useDeferredRender(3000);
  const sectionRef = useRef(null);
  const scrollProgressRef = useRef(0);
  let t0;
  let t1;
  if ($[0] !== isDesktop || $[1] !== sceneReady) {
    t0 = () => {
      if (!isDesktop || !sceneReady || !sectionRef.current) {
        return;
      }
      gsap.registerPlugin(ScrollTrigger);
      const st = ScrollTrigger.create({
        trigger: sectionRef.current,
        start: "top top",
        end: "bottom top",
        scrub: true,
        onUpdate: self => {
          scrollProgressRef.current = self.progress;
        }
      });
      return () => {
        st.kill();
      };
    };
    t1 = [isDesktop, sceneReady];
    $[0] = isDesktop;
    $[1] = sceneReady;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  if ($[4] !== isDesktop || $[5] !== sceneReady) {
    t2 = isDesktop && sceneReady ? <div className="pointer-events-none absolute inset-0"><HeroScene scrollProgressRef={scrollProgressRef} /></div> : <div className="pointer-events-none absolute inset-0 bg-linear-to-br from-teal-50/40 via-white to-cyan-50/30" />;
    $[4] = isDesktop;
    $[5] = sceneReady;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="pointer-events-none absolute inset-0"><div className="absolute inset-y-0 left-0 w-full bg-linear-to-r from-white via-white/90 to-transparent sm:w-3/5" /><div className="absolute inset-x-0 bottom-0 h-32 bg-linear-to-t from-white to-transparent" /></div>;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="mr-2 inline-block size-1.5 animate-pulse rounded-full bg-teal-500" />;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t) {
    t5 = t("landing.hero.badge");
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t5) {
    t6 = <div className="mb-6 inline-flex items-center rounded-full border border-teal-200/60 bg-teal-50/80 px-4 py-1.5 text-xs font-medium text-teal-700 backdrop-blur-sm">{t4}{t5}</div>;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t) {
    t7 = t("landing.hero.title1");
    $[13] = t;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <br />;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t) {
    t9 = t("landing.hero.title2");
    $[16] = t;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t9) {
    t10 = <span className="bg-linear-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">{t9}</span>;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t7) {
    t11 = <h1 className="text-3xl leading-[1.08] font-bold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">{t7}{t8}{t10}</h1>;
    $[20] = t10;
    $[21] = t7;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t) {
    t12 = t("landing.hero.subtitle");
    $[23] = t;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t12) {
    t13 = <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-500 sm:text-lg">{t12}</p>;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t) {
    t14 = t("landing.hero.cta");
    $[27] = t;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== t14) {
    t16 = <Link href="/auth/login" className="group flex items-center gap-2 rounded-xl bg-slate-900 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-slate-900/10 transition-all hover:bg-slate-800 hover:shadow-xl">{t14}{t15}</Link>;
    $[30] = t14;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Play className="size-3.5 text-teal-600" />;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t) {
    t18 = t("landing.hero.ctaSecondary");
    $[33] = t;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== t18) {
    t19 = <a href="#showcase" onClick={_temp} className="group flex items-center gap-2 rounded-xl border border-slate-200 bg-white/80 px-6 py-3 text-sm font-medium text-slate-600 backdrop-blur-sm transition-all hover:border-slate-300 hover:bg-white">{t17}{t18}</a>;
    $[35] = t18;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== t16 || $[38] !== t19) {
    t20 = <div className="mt-8 flex flex-wrap items-center gap-3">{t16}{t19}</div>;
    $[37] = t16;
    $[38] = t19;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  let t21;
  if ($[40] !== t) {
    t21 = t("landing.hero.trustRegions");
    $[40] = t;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== t21) {
    t22 = <TrustStat value="13" label={t21} />;
    $[42] = t21;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <div className="hidden h-8 w-px bg-slate-200/60 sm:block" />;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] !== t) {
    t24 = t("landing.hero.trustUpdates");
    $[45] = t;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  let t25;
  if ($[47] !== t24) {
    t25 = <TrustStat value="24/7" label={t24} />;
    $[47] = t24;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  let t26;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <div className="hidden h-8 w-px bg-slate-200/60 sm:block" />;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== t) {
    t27 = t("landing.hero.trustFree");
    $[50] = t;
    $[51] = t27;
  } else {
    t27 = $[51];
  }
  let t28;
  if ($[52] !== t27) {
    t28 = <TrustStat value="100%" label={t27} />;
    $[52] = t27;
    $[53] = t28;
  } else {
    t28 = $[53];
  }
  let t29;
  if ($[54] !== t22 || $[55] !== t25 || $[56] !== t28) {
    t29 = <div className="mt-8 grid grid-cols-3 gap-2 border-t border-slate-200/60 pt-5 sm:mt-10 sm:flex sm:items-center sm:gap-6 sm:pt-6">{t22}{t23}{t25}{t26}{t28}</div>;
    $[54] = t22;
    $[55] = t25;
    $[56] = t28;
    $[57] = t29;
  } else {
    t29 = $[57];
  }
  let t30;
  if ($[58] !== t11 || $[59] !== t13 || $[60] !== t20 || $[61] !== t29 || $[62] !== t6) {
    t30 = <div className="relative z-10 mx-auto flex min-h-[calc(100svh-3.5rem)] max-w-7xl items-start px-4 pt-20 pb-16 sm:min-h-[70vh] sm:items-center sm:px-6 sm:pt-0 sm:pb-0"><div className="max-w-xl">{t6}{t11}{t13}{t20}{t29}</div></div>;
    $[58] = t11;
    $[59] = t13;
    $[60] = t20;
    $[61] = t29;
    $[62] = t6;
    $[63] = t30;
  } else {
    t30 = $[63];
  }
  let t31;
  if ($[64] !== t2 || $[65] !== t30) {
    t31 = <section ref={sectionRef} className="relative min-h-[calc(100svh-3.5rem)] overflow-hidden sm:min-h-[70vh]">{t2}{t3}{t30}</section>;
    $[64] = t2;
    $[65] = t30;
    $[66] = t31;
  } else {
    t31 = $[66];
  }
  return t31;
}
function _temp(e) {
  const el = document.getElementById("showcase");
  if (el) {
    e.preventDefault();
    el.scrollIntoView({
      behavior: "smooth"
    });
  }
}
function TrustStat(t0) {
  const $ = _c(7);
  const {
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== value) {
    t1 = <p className="text-lg font-bold text-slate-900">{value}</p>;
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <p className="text-xs leading-tight text-slate-500">{label}</p>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="min-w-0 text-center sm:text-left">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}